import { useCallback } from 'react';
import { css } from '@emotion/react';
import { RefundFilterType, RefundModel, RefundSearchParams } from '@frontend/api-refunds';
import { PrintDialog } from '@frontend/assets';
import { InfinitePaginatedList } from '@frontend/components';
import { formatCentsToCurrency } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import { CardBrand } from '@frontend/payments-card-brand';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  Avatar,
  Chip,
  ContentLoader,
  formatDate,
  ListRow,
  NakedUl,
  PrintIcon,
  Table,
  Text,
  UpdateIcon,
  useAlert,
} from '@frontend/design-system';
import { useRefundShallowStore } from '../../../hooks/refund-provider';
import { useCanDoActionsMulti } from '../../../hooks/use-can-do-actions-multi';
import { useGenerateColumns } from '../../../hooks/use-generate-columns';
import { useRefundSearch, useQueryAllRefunds, useRefundSearchInfiniteQuery } from '../../../hooks/use-refund-search';
import { getPaymentMethodBrand, PaymentsTableInstances } from '../../../utils/helpers';
import { PrintHeader } from '../../PrintHeader';
import { SkeletonListLoader } from '../../Shared/skeleton-list-loader';
import { RefundFilter } from '../RefundFilter';
import { RefundStatusChip } from '../RefundStatusChip';
import { generateColumns } from './generate-columns';
import { useRefundListCsvExport } from './use-refund-list-csv-export';

const FIELD_TO_ORDER: { [key: string]: RefundSearchParams['order'] } = {
  billedAmount: 'billedamount',
  billedAt: 'billedAt',
  paidAmount: 'paidamount',
  paidAt: 'paidat',
};

export type RefundListProps = { handlePrint: () => void; onChangeFilter?: (filter: RefundFilterType) => void };

export const RefundList = ({ handlePrint, onChangeFilter }: RefundListProps) => {
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const { t } = useTranslation('payments');
  const navigate = useNavigate();
  const alert = useAlert();
  const { selectedLocationIds } = useAppScopeStore();
  const { canExport } = useCanDoActionsMulti(selectedLocationIds, 'any');
  const columns = useGenerateColumns<RefundModel>(generateColumns);

  // Refund Provider
  const { numRows, setNumRows, setOrder, filter, setFilter, resetPageParams } = useRefundShallowStore(
    'numRows',
    'setNumRows',
    'setOrder',
    'filter',
    'setFilter',
    'resetPageParams'
  );

  const { refunds, refetch, loading, hasMore, hasPrevious, fetchNextPage, fetchPreviousPage, currentPage } =
    useRefundSearch();

  const { ExportAction, InvoiceExportModal } = useRefundListCsvExport({
    dateRange: { start: filter.dateRefunded?.gte, end: filter.dateRefunded?.lte },
    hasSearchParams: !!filter.personName,
    hasFiltersOtherThanDates: Object.keys(filter).filter((value) => value !== 'dateRefunded').length > 0,
    canExport,
  });

  const handlePrintClick = () => {
    if (!refunds.length) {
      alert.error('No data to print. Apply a filter and try again.');
    } else {
      handlePrint();
    }
  };

  const handleChangeFilter = useCallback(
    (toSet: RefundFilterType) => {
      setFilter(toSet);
      onChangeFilter?.(toSet);
    },
    [onChangeFilter, setFilter]
  );

  return (
    <>
      {isMobile ? (
        <MobileRefundList />
      ) : (
        <>
          <Table
            tableStyle={css`
              min-height: 500px;
              height: 100%;
            `}
            globalTrackingId='pay-portal-refunds'
            colConfig={columns}
            data={refunds}
            tableInstanceId={PaymentsTableInstances.Refunds}
            isLoading={loading}
            loadingRowsCount={numRows}
            manualSortBy
            hasResizeColumns
            hasResponsiveColWidths
            manualFilters
            manualFiltersRender={(modalProps, setShowNotification) => (
              <RefundFilter
                data-trackingid='pay-portal-refunds-btn-filter'
                initialFilter={filter}
                onApply={handleChangeFilter}
                modalProps={modalProps}
                setShowNotification={setShowNotification}
              />
            )}
            emptyStateConfig={{
              type: 'payments',
              header: t('No data to display'),
            }}
            tableActions={[
              {
                label: t('Print'),
                Icon: PrintIcon,
                onClick: handlePrintClick,
              },
              ...(canExport ? [ExportAction] : []),
              {
                label: t('Refresh'),
                Icon: UpdateIcon,
                onClick: () => refetch(),
              },
            ]}
            styleConfig={{
              columns: [
                {
                  id: '*',
                  cellStyler: css`
                    padding: ${theme.spacing(1, 1.5)};
                  `,
                  headerStyler: css`
                    padding: ${theme.spacing(2, 1.5)};
                  `,
                },
              ],
            }}
            onSortChange={(res) => {
              resetPageParams();
              if (!res.length) {
                setOrder('-paidat');
                return;
              }
              const { id, value } = res[0] ?? {};
              if (id) {
                const order = FIELD_TO_ORDER[id];
                // fallback to -billedAt if field is unsupported
                setOrder(
                  order
                    ? (`${value === 'asc' ? '' : '-'}${FIELD_TO_ORDER[id]}` as RefundSearchParams['order'])
                    : '-billedAt'
                );
              } else {
                setOrder('-billedAt');
              }
            }}
            rowActions={{
              onRowClick: (refund: RefundModel) => {
                if (refund.invoiceId) {
                  navigate({ to: '/payments/refunds/$invoiceId', params: { invoiceId: refund.invoiceId } });
                }
              },
            }}
            isPaginated
            manualPaginationConfig={{
              page: currentPage,
              hasNext: hasMore && !loading,
              hasPrevious: hasPrevious && currentPage !== 1 && !loading,
              onNumRowsChange: (num) => {
                setNumRows(num);
                resetPageParams();
              },
              defaultRowsPerPage: numRows,
              rowsPerPageOptions: [10, 25, 50, 75, 100],
              handleChange: (prevOrNext: 'prev' | 'next') => {
                if (prevOrNext === 'prev') fetchPreviousPage();
                if (prevOrNext === 'next') fetchNextPage();
              },
            }}
            hasGlobalSearch={true}
            globalSearchConfig={{
              initialValue: filter.personName || '',
              position: 'right',
              searchHandler: (searchText: string) => {
                const toSet = {
                  ...filter,
                  personName: searchText.trim().length > 0 ? searchText : undefined,
                };

                if (toSet.personName !== filter.personName) {
                  setFilter(toSet);
                }
              },
              placeholder: t('Search'),
              debounceDelay: 1000,
            }}
          />
        </>
      )}
      {InvoiceExportModal}
    </>
  );
};

const MobileRefundList = () => {
  const { t } = useTranslation('payments');
  const navigate = useNavigate();
  const { accessibleLocationData } = useAppScopeStore();
  const getLocationName = (locationId: string) => accessibleLocationData[locationId]?.name ?? '';
  const refundInfiniteQueryProps = useRefundSearchInfiniteQuery({
    select: (data) => ({
      pageParams: data.pageParams,
      pages: data?.pages.map((page) => ({
        rows: page?.refunds?.edges?.map((edge) => edge.node) || [],
      })),
    }),
  });
  const RenderListItem = ({ listItem }: { listItem: RefundModel }) => {
    return (
      <ListRow
        as='div'
        css={{ gridTemplateAreas: '"lead content"' }}
        onClick={() => {
          if (listItem.invoiceId) {
            navigate({ to: '/payments/refunds/$invoiceId', params: { invoiceId: listItem.invoiceId } });
          }
        }}
      >
        <ListRow.Lead>
          <Avatar name={listItem.person.name} />
        </ListRow.Lead>
        <ListRow.Content>
          <ListRow.Content.Title
            as='div'
            css={{
              alignItems: 'flex-start',
              display: 'flex',
              gap: theme.spacing(1),
              justifyContent: 'space-between',
              paddingRight: 0,
            }}
          >
            <span>{listItem.person.name}</span>
            <RefundStatusChip refund={listItem} />
          </ListRow.Content.Title>
          <ListRow.Content.Subtitle as='div'>
            {listItem.locationId && <Chip.Location maxWidth=''>{getLocationName(listItem.locationId)}</Chip.Location>}
          </ListRow.Content.Subtitle>
          <ListRow.Content.TertiaryTitle as='div'>
            <NakedUl
              css={css`
                display: flex;
                flex-direction: row;
                gap: ${theme.spacing(3)};
                li {
                  display: flex;
                  flex-direction: column;
                }
              `}
            >
              <li>
                <Text size='small' color='light'>
                  {t('Refund Amount')}
                </Text>
                <Text>{formatCentsToCurrency(listItem.refundAmount)}</Text>
              </li>
              <li>
                <Text size='small' color='light'>
                  {t('Payment Method')}
                </Text>
                <CardBrand
                  brand={getPaymentMethodBrand({
                    paymentType: listItem.payment?.paymentType,
                    brand: listItem.payment?.brand,
                    bankName: listItem.payment?.bankName,
                  })}
                  last4={listItem.payment?.last4}
                />
              </li>
              <li>
                <Text size='small' color='light'>
                  {t('Date Created')}
                </Text>
                <Text>{formatDate(listItem.dateRefunded, 'MMM D, YYYY')}</Text>
              </li>
            </NakedUl>
          </ListRow.Content.TertiaryTitle>
        </ListRow.Content>
      </ListRow>
    );
  };

  return refundInfiniteQueryProps.isLoading ? (
    <SkeletonListLoader />
  ) : (
    <InfinitePaginatedList
      emptyStateConfig={{ type: 'payments', header: t('No data to display') }}
      renderListItem={RenderListItem}
      infiniteQueryProps={refundInfiniteQueryProps}
    />
  );
};

export type PrintRefundListProps = {
  onClose: () => void;
  headerContent?: React.ReactNode;
};

export const PrintRefundList = ({ onClose, headerContent }: PrintRefundListProps) => {
  const { allRefunds, allRefundsLoading } = useQueryAllRefunds();
  const columns = useGenerateColumns<RefundModel>(generateColumns, true, PaymentsTableInstances.Refunds);

  return allRefundsLoading ? (
    <ContentLoader show={allRefundsLoading} />
  ) : !allRefunds?.length ? null : (
    <PrintDialog show={true} onClose={onClose}>
      <div
        css={css`
          margin: ${theme.spacing(2)};
        `}
      >
        <PrintHeader />
        {headerContent}
        <Table colConfig={columns} data={allRefunds} tableInstanceId={PaymentsTableInstances.Refunds} />
      </div>
    </PrintDialog>
  );
};
